import {
  Play,
  Upload,
  Loader2,
  Trash2,
  History,
  Info,
  Users,
  ChevronDown,
  Plus,
  Tag,
  Settings,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@f2e/ui/components/base"
import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { Agent, AgentHistory, EvaluationCaseLabel } from "@/types"
import { AgentHistoryDropdownState } from "@/hooks/useAgentHistories"
import { AgentHistoryDropdownBase } from "@/components/Agent/AgentHistoryDropdownBase"
import { AgentHistoryDetailedItem } from "@/components/Agent/AgentHistoryDetailedItem"

import { Evaluator } from "./types"

export interface EvaluationTableHeaderProps {
  runAllLoading: boolean
  runSelectedLoading: boolean
  deleteLoading: boolean
  selectedCount: number
  runAllRunnableCount: number
  runSelectedRunnableCount: number
  runAllDisabled: boolean
  runSelectedDisabled?: boolean
  caseLimitReached: boolean
  currentAgent?: Agent
  agentHistoryState: AgentHistoryDropdownState
  evaluators: Evaluator[]
  selectedEvaluator?: Evaluator
  testerSelectionDisabled?: boolean
  labels?: EvaluationCaseLabel[]
  selectedLabelIds?: string[]
  onRunAll?: () => void
  onRunSelected?: () => void
  onUploadCSV?: () => void
  onCopySelected?: () => void
  onDeleteSelected?: () => void
  onSelectEvaluator?: (evaluator: Evaluator | null) => void
  onSelectLabelIds?: (labelIds: string[]) => void
  onManageLabels?: () => void
  onManageEvaluators?: () => void
  onAddCase?: () => void
}

const EvaluationTableHeader = ({
  runAllLoading,
  runSelectedLoading,
  deleteLoading,
  selectedCount,
  runAllRunnableCount,
  runSelectedRunnableCount,
  runAllDisabled,
  runSelectedDisabled,
  caseLimitReached,
  currentAgent,
  agentHistoryState,
  evaluators,
  selectedEvaluator,
  testerSelectionDisabled = false,
  labels = [],
  selectedLabelIds = [],
  onRunAll,
  onRunSelected,
  onUploadCSV,
  onCopySelected,
  onDeleteSelected,
  onAddCase,
  onSelectEvaluator,
  onSelectLabelIds,
  onManageLabels,
  onManageEvaluators,
}: EvaluationTableHeaderProps) => {
  const { t } = useTranslation()

  const handleEvaluatorSelection = (evaluator: Evaluator | null) => {
    onSelectEvaluator?.(evaluator)
  }

  const hasAgentHistory = Boolean(agentHistoryState.currentHistory?.id)
  const testerName = selectedEvaluator?.name || ""
  const runAllTooltip = !selectedEvaluator
    ? t("evaluation.evaluationTable.action.selectTesterToRun")
    : !hasAgentHistory
      ? t("evaluation.evaluationTable.action.selectAgentVersionToRun")
      : runAllRunnableCount === 0
        ? t("evaluation.evaluationTable.action.noAssignedCasesForTester")
        : t("evaluation.evaluationTable.action.runAllScope", {
            count: runAllRunnableCount,
            tester: testerName,
          })
  const runSelectedTooltip = !selectedEvaluator
    ? t("evaluation.evaluationTable.action.selectTesterToRun")
    : !hasAgentHistory
      ? t("evaluation.evaluationTable.action.selectAgentVersionToRun")
      : selectedCount === 0
        ? t("evaluation.evaluationTable.action.selectCasesToRun")
        : runSelectedRunnableCount === 0
          ? t(
              "evaluation.evaluationTable.action.selectedCasesNotAssignedToTester"
            )
          : t("evaluation.evaluationTable.action.runSelectedScope", {
              runnableCount: runSelectedRunnableCount,
              selectedCount,
              tester: testerName,
            })
  const showSelectedRunnableHint =
    selectedCount > 0 &&
    runSelectedRunnableCount > 0 &&
    runSelectedRunnableCount < selectedCount

  const selectedLabelNames = labels
    .filter((label) => selectedLabelIds.includes(label.id))
    .map((label) => label.name)

  const handleLabelSelection = (labelId: string, checked: boolean) => {
    onSelectLabelIds?.(
      checked
        ? [...selectedLabelIds, labelId]
        : selectedLabelIds.filter((id) => id !== labelId)
    )
  }

  return (
    <div className="w-full bg-muted/50 dark:bg-slate-800/50 rounded-xl border border-muted dark:border-slate-700 p-6 mb-6">
      <TooltipProvider delayDuration={100}>
        <div className="space-y-4">
          {/* Primary Action Buttons Row */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Hidden fields for header state */}
            <CopilotHiddenField
              fieldKey="header.selection.count"
              value={String(selectedCount || 0)}
            />
            <CopilotHiddenField
              fieldKey="header.runAll.disabled"
              value={runAllDisabled ? "true" : "false"}
            />
            <CopilotHiddenField
              fieldKey="header.caseLimitReached"
              value={caseLimitReached ? "true" : "false"}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white dark:text-gray-900 rounded-lg hover:bg-primary/90 dark:hover:bg-primary/80 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={onRunAll}
                    disabled={runAllLoading || !!runAllDisabled}
                  >
                    {runAllLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    <span className="font-medium text-sm">
                      {runAllLoading
                        ? t("evaluation.evaluationTable.action.running")
                        : t("evaluation.evaluationTable.action.runAll")}
                    </span>
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{runAllTooltip}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-900 text-primary dark:text-primary rounded-lg border border-primary/40 dark:border-primary/40 hover:bg-primary/5 dark:hover:bg-primary/10 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-500 disabled:border-slate-200 dark:disabled:border-slate-600 disabled:cursor-not-allowed transition-all"
                    onClick={onRunSelected}
                    disabled={
                      runSelectedLoading ||
                      !!runSelectedDisabled ||
                      selectedCount === 0
                    }
                  >
                    {runSelectedLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    <span className="font-medium text-sm">
                      {runSelectedLoading
                        ? t("evaluation.evaluationTable.action.running")
                        : t("evaluation.evaluationTable.action.runSelected", {
                            count: runSelectedRunnableCount,
                          })}
                    </span>
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{runSelectedTooltip}</TooltipContent>
            </Tooltip>

            {showSelectedRunnableHint && (
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                {t("evaluation.evaluationTable.action.selectedRunnableHint", {
                  runnableCount: runSelectedRunnableCount,
                  selectedCount,
                })}
              </span>
            )}

            <button
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white dark:bg-blue-400 dark:text-gray-900 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-500 transition disabled:opacity-60"
              onClick={onAddCase}
              disabled={caseLimitReached}
            >
              <Plus className="w-4 h-4" />
              <span className="font-medium text-sm">
                {t("evaluation.evaluationTable.action.addEvaluationCase")}
              </span>
            </button>

            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 rounded-lg border border-blue-200 dark:border-blue-600 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
              onClick={onUploadCSV}
            >
              <Upload className="w-4 h-4" />
              <span className="font-medium text-sm">
                {t("evaluation.evaluationTable.action.uploadCSV")}
              </span>
            </button>

            <button
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-900 text-blue-600 dark:text-blue-400 rounded-lg border border-blue-200 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-500 disabled:border-slate-200 dark:disabled:border-slate-600 disabled:cursor-not-allowed transition-all"
              onClick={onCopySelected}
              disabled={selectedCount === 0}
            >
              <span className="font-medium text-sm">
                {t("evaluation.evaluationTable.action.copySelectedReasons")}
              </span>
            </button>

            <button
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-900 text-red-600 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-500 disabled:border-slate-200 dark:disabled:border-slate-600 disabled:cursor-not-allowed transition-all"
              onClick={onDeleteSelected}
              disabled={selectedCount === 0 || deleteLoading}
              title={t("evaluation.evaluationTable.action.deleteSelected")}
            >
              {deleteLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              <span className="font-medium text-sm">
                {deleteLoading
                  ? t("evaluation.evaluationTable.action.deleting")
                  : t("evaluation.evaluationTable.action.deleteSelected")}
              </span>
            </button>
          </div>

          {/* Configuration Controls Row */}
          <div className="flex items-center justify-between flex-wrap gap-4 pt-2 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Agent Version Dropdown */}
              {currentAgent && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                    <History className="w-4 h-4 text-slate-500" />
                    {t("evaluation.evaluationTable.header.agentVersion")}
                  </label>
                  <AgentHistoryDropdownBase
                    agentId={currentAgent.id}
                    state={agentHistoryState}
                    renderItem={(history: AgentHistory, isCurrent: boolean) => (
                      <AgentHistoryDetailedItem
                        history={history}
                        isCurrent={isCurrent}
                      />
                    )}
                    renderTrigger={(currentHistory: AgentHistory | null) => (
                      <button
                        className="text-sm px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-md text-slate-700 dark:text-slate-300 flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 transition-all"
                        title={currentHistory?.versionNote}
                        aria-label={[
                          currentAgent.name,
                          currentHistory
                            ? `v${currentHistory.versionNumber}`
                            : t(
                                "evaluation.evaluationTable.header.currentVersion"
                              ),
                          currentHistory?.versionNote,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <span className="shrink-0">
                          {currentAgent.name}{" "}
                          {currentHistory
                            ? `v${currentHistory.versionNumber}`
                            : t(
                                "evaluation.evaluationTable.header.currentVersion"
                              )}
                        </span>
                        {currentHistory?.versionNote && (
                          <span className="truncate max-w-[180px] text-xs text-slate-500 dark:text-slate-400">
                            {currentHistory.versionNote}
                          </span>
                        )}
                        <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                      </button>
                    )}
                  />
                  <CopilotHiddenField
                    fieldKey="header.agentVersion.selected"
                    value={`${currentAgent.name}; version=${
                      agentHistoryState?.currentHistory
                        ? `v${agentHistoryState.currentHistory.versionNumber}`
                        : "current"
                    }`}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-4 h-4 text-slate-400 cursor-pointer hover:text-slate-500 transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      sideOffset={8}
                      className="max-w-xs text-sm z-9999"
                    >
                      {t("evaluation.evaluationTable.tooltip.agentVersion")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}

              {/* Evaluator Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  {t("evaluation.evaluationTable.header.evaluator")}
                </label>
                <CopilotHiddenField
                  fieldKey="header.evaluator.options"
                  value={evaluators
                    .map((e) =>
                      String(e.name ?? "")
                        .replace(/[|\n]/g, " ")
                        .trim()
                    )
                    .join(" | ")}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="text-sm px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-md text-slate-700 dark:text-slate-300 flex items-center gap-2 min-w-[140px] justify-between hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        evaluators.length === 0 || testerSelectionDisabled
                      }
                    >
                      <span className="truncate">
                        {selectedEvaluator?.name ||
                          (evaluators.length > 0
                            ? t("evaluation.evaluationTable.header.allTesters")
                            : "No evaluators")}
                      </span>
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <CopilotHiddenField
                    fieldKey="header.evaluator.selected"
                    value={selectedEvaluator?.name || ""}
                  />

                  <DropdownMenuContent className="max-h-48 overflow-y-auto min-w-[140px]">
                    <DropdownMenuItem
                      onClick={() => handleEvaluatorSelection(null)}
                    >
                      {t("evaluation.evaluationTable.header.allTesters")}
                    </DropdownMenuItem>
                    {evaluators.map((ev) => (
                      <DropdownMenuItem
                        key={ev.id}
                        onClick={() => handleEvaluatorSelection(ev)}
                      >
                        {ev.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-slate-400 cursor-pointer hover:text-slate-500 transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    sideOffset={8}
                    className="max-w-xs text-sm z-9999"
                  >
                    {t("evaluation.evaluationTable.tooltip.evaluator")}
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Labels Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                  <Tag className="w-4 h-4 text-slate-500" />
                  {t("evaluation.evaluationTable.header.labels")}
                </label>
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-sm px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-md text-slate-700 dark:text-slate-300 flex items-center gap-2 min-w-[140px] max-w-[260px] justify-between hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-500 transition-all">
                        <span className="truncate">
                          {selectedLabelNames.length > 0
                            ? selectedLabelNames.join(", ")
                            : t("evaluation.evaluationTable.header.allLabels")}
                        </span>
                        <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-64 min-w-[220px] overflow-y-auto">
                      {labels.length === 0 ? (
                        <DropdownMenuItem disabled>
                          {t("evaluation.evaluationTable.table.noLabels")}
                        </DropdownMenuItem>
                      ) : (
                        labels.map((label) => {
                          const checked = selectedLabelIds.includes(label.id)
                          return (
                            <DropdownMenuItem
                              key={label.id}
                              onSelect={(event) => event.preventDefault()}
                              className="flex items-center gap-2"
                              onClick={() =>
                                handleLabelSelection(label.id, !checked)
                              }
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                readOnly
                              />
                              <span className="truncate">{label.name}</span>
                            </DropdownMenuItem>
                          )
                        })
                      )}
                      {selectedLabelIds.length > 0 && (
                        <DropdownMenuItem
                          onClick={() => onSelectLabelIds?.([])}
                        >
                          {t("evaluation.evaluationTable.header.clearLabels")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button
                    type="button"
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    onClick={onManageLabels}
                    title={t("evaluation.evaluationTable.action.manageLabels")}
                    aria-label={t(
                      "evaluation.evaluationTable.action.manageLabels"
                    )}
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Manage Evaluators Button */}
            <button
              onClick={onManageEvaluators}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t("evaluation.evaluationTable.action.manageEvaluators")}
              </span>
            </button>
          </div>
        </div>
      </TooltipProvider>
    </div>
  )
}

export default EvaluationTableHeader
